import { Check } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './Toast.module.css';

const TOAST_MS = 2600;

type ShowToast = (text: string) => void;

const ToastContext = createContext<ShowToast | null>(null);

/** Single black pill at the bottom centre, as in the design. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const show = useCallback<ShowToast>((message) => {
    clearTimeout(timer.current);
    setText(message);
    timer.current = setTimeout(() => setText(''), TOAST_MS);
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className={styles.region} role="status" aria-live="polite">
        {text ? (
          <div className={styles.toast} key={text}>
            <span className={styles.icon}>
              <Check size={12} strokeWidth={3} />
            </span>
            <span>{text}</span>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
